"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.labMasterSetActiveBodySchema = exports.labMasterListQuerySchema = exports.labMasterAnyItemParamSchema = exports.labMasterAnyKindParamSchema = exports.labMasterItemParamSchema = exports.labMasterKindParamSchema = exports.LAB_MASTER_ACTIVATION_KIND_KEYS = exports.LAB_MASTER_USAGE_LABELS = exports.LAB_MASTER_KINDS = exports.LAB_MASTER_KIND_KEYS = void 0;
exports.buildLabMasterCreateSchema = buildLabMasterCreateSchema;
exports.buildLabMasterUpdateSchema = buildLabMasterUpdateSchema;
/**
 * LIS MASTERS ADMIN — the one description of every flat laboratory master.
 *
 * WHY A REGISTRY AND NOT NINE SCHEMAS. Disciplines, specimen types, containers,
 * methods, analyzers, rejection reasons, result value sets, organisms and
 * antibiotics are the same shape of thing: a coded row with a handful of
 * descriptive columns that a laboratory administrator creates, corrects and
 * switches off. Writing nine controllers would give nine subtly different sets
 * of rules (which trim, which upper-case, which refuse a duplicate code). One
 * registry gives the server its validation and the screen its form from the SAME
 * declaration, so a field cannot exist on one side and not the other.
 *
 * NOT IN HERE, DELIBERATELY:
 *   - Parameters and Units already have their own create/update endpoints with
 *     rules of their own (source reference, result-type guards; the shared lookup
 *     list). The Masters screen calls those; only their on/off switch is generic.
 *   - Report templates, clinical mapping reviews, reference ranges, critical
 *     values, delta rules and TAT targets are APPROVAL workflows (draft, review,
 *     approve, retire). Making them "editable" by a plain form would bypass the
 *     approval that makes them clinically safe.
 */
const zod_1 = require("zod");
exports.LAB_MASTER_KIND_KEYS = [
    'disciplines',
    'specimen-types',
    'containers',
    'methods',
    'analyzers',
    'rejection-reasons',
    'value-sets',
    'organisms',
    'antibiotics',
];
const code = (max = 30, help) => ({
    key: 'code',
    label: 'Code',
    type: 'text',
    required: true,
    maxLength: max,
    upper: true,
    help: help ?? 'Short and unique, e.g. HAEM. Other systems refer to it, so it cannot change once the master is in use.',
});
const displayOrder = {
    key: 'displayOrder',
    label: 'Display order',
    type: 'integer',
    min: 0,
    max: 9999,
    help: 'Lower numbers list first.',
};
exports.LAB_MASTER_KINDS = {
    disciplines: {
        key: 'disciplines',
        label: 'Disciplines',
        singular: 'Discipline',
        blurb: 'The laboratory sections a test belongs to — Haematology, Biochemistry, Microbiology.',
        nameField: 'name',
        identityFields: ['code'],
        columns: ['shortName', 'departmentId', 'displayOrder'],
        usage: ['tests', 'templates', 'analyzers', 'LabParameter', 'tatTargets'],
        fields: [
            code(),
            { key: 'name', label: 'Name', type: 'text', required: true, maxLength: 150 },
            { key: 'shortName', label: 'Short name', type: 'text', maxLength: 60 },
            { key: 'departmentId', label: 'Department', type: 'ref', ref: 'department' },
            {
                key: 'legacyCodePrefix',
                label: 'Legacy code prefix',
                type: 'text',
                maxLength: 10,
                upper: true,
                help: 'The prefix the old charge codes used ("BC", "CPH"). Only read when importing.',
            },
            displayOrder,
            { key: 'description', label: 'Description', type: 'textarea', maxLength: 2000 },
        ],
    },
    'specimen-types': {
        key: 'specimen-types',
        label: 'Specimen types',
        singular: 'Specimen type',
        blurb: 'What is collected — Whole blood, Serum, Urine — with its default container and handling.',
        nameField: 'name',
        identityFields: ['code'],
        columns: ['category', 'parentId', 'defaultContainerId'],
        usage: ['children', 'tests', 'parameters', 'referenceRanges', 'rejectionReasons', 'containers', 'testSpecimens', 'criticalValues', 'deltaRules', 'LabSpecimen'],
        fields: [
            code(),
            { key: 'name', label: 'Name', type: 'text', required: true, maxLength: 150 },
            { key: 'displayName', label: 'Display name', type: 'text', maxLength: 150, help: 'What prints on labels and reports, if different from the name.' },
            { key: 'category', label: 'Category', type: 'text', maxLength: 50 },
            { key: 'parentId', label: 'Parent specimen', type: 'ref', ref: 'specimen-types', help: 'Group this under a broader specimen, e.g. Serum under Blood.' },
            { key: 'defaultContainerId', label: 'Default container', type: 'ref', ref: 'containers' },
            { key: 'defaultMinimumVolume', label: 'Minimum volume', type: 'decimal', min: 0, max: 9999999 },
            { key: 'volumeUnitId', label: 'Volume unit', type: 'ref', ref: 'unit' },
            { key: 'defaultTransportTemperature', label: 'Transport temperature', type: 'text', maxLength: 50 },
            { key: 'defaultStabilityHours', label: 'Stable for (hours)', type: 'integer', min: 0, max: 100000 },
            { key: 'collectionInstruction', label: 'Collection instruction', type: 'textarea', maxLength: 2000 },
            displayOrder,
            { key: 'description', label: 'Description', type: 'textarea', maxLength: 2000 },
        ],
    },
    containers: {
        key: 'containers',
        label: 'Containers',
        singular: 'Container',
        blurb: 'Tubes and pots. A container is identified by tube type and additive — cap colour is only a picking aid.',
        nameField: 'name',
        identityFields: ['code'],
        columns: ['tubeType', 'additive', 'capColour'],
        usage: ['tests', 'specimenTypes', 'specimenLinks', 'testSpecimens', 'LabSpecimen'],
        fields: [
            code(),
            { key: 'name', label: 'Name', type: 'text', required: true, maxLength: 150 },
            { key: 'tubeType', label: 'Tube type', type: 'text', maxLength: 60 },
            { key: 'additive', label: 'Additive', type: 'text', maxLength: 100 },
            { key: 'material', label: 'Material', type: 'text', maxLength: 60 },
            { key: 'capColour', label: 'Cap colour', type: 'text', maxLength: 50, help: 'A local convention — never used to match anything.' },
            { key: 'drawOrder', label: 'Order of draw', type: 'integer', min: 0, max: 9999 },
            { key: 'defaultVolume', label: 'Default volume', type: 'decimal', min: 0, max: 9999999 },
            { key: 'volumeUnitId', label: 'Volume unit', type: 'ref', ref: 'unit' },
            { key: 'description', label: 'Description', type: 'textarea', maxLength: 2000 },
        ],
    },
    methods: {
        key: 'methods',
        label: 'Methods',
        singular: 'Method',
        blurb: 'Measurement principles. A method is how a result is produced; an analyzer is what produces it.',
        nameField: 'name',
        identityFields: ['code'],
        columns: ['category', 'principle', 'manufacturer'],
        usage: ['tests', 'parameters', 'analyzerTests', 'referenceRanges', 'testMethods', 'criticalValues'],
        fields: [
            code(),
            { key: 'name', label: 'Name', type: 'text', required: true, maxLength: 255 },
            { key: 'displayName', label: 'Display name', type: 'text', maxLength: 255 },
            { key: 'category', label: 'Category', type: 'text', maxLength: 60, help: 'e.g. IMMUNOASSAY, PHOTOMETRY, ISE, HAEMATOLOGY.' },
            { key: 'principle', label: 'Principle', type: 'text', maxLength: 255 },
            { key: 'manufacturer', label: 'Manufacturer', type: 'text', maxLength: 255 },
            { key: 'measuringRangeLow', label: 'Measuring range from', type: 'decimal' },
            { key: 'measuringRangeHigh', label: 'Measuring range to', type: 'decimal' },
            { key: 'effectiveFrom', label: 'In use from', type: 'date' },
            { key: 'effectiveTo', label: 'In use until', type: 'date' },
            { key: 'description', label: 'Description', type: 'textarea', maxLength: 2000 },
        ],
    },
    analyzers: {
        key: 'analyzers',
        label: 'Analyzers',
        singular: 'Analyzer',
        blurb: 'Instrument inventory. Interface status is recorded only — nothing is transmitted to or from an instrument.',
        nameField: 'name',
        identityFields: ['code'],
        columns: ['make', 'model', 'analyzerType', 'interfaceStatus'],
        usage: ['tests', 'analyzerTests', 'referenceRanges', 'criticalValues'],
        fields: [
            code(),
            { key: 'name', label: 'Name', type: 'text', required: true, maxLength: 255 },
            { key: 'make', label: 'Make', type: 'text', maxLength: 150 },
            { key: 'model', label: 'Model', type: 'text', maxLength: 150 },
            { key: 'serialNumber', label: 'Serial number', type: 'text', maxLength: 100 },
            { key: 'location', label: 'Location', type: 'text', maxLength: 255 },
            { key: 'disciplineId', label: 'Discipline', type: 'ref', ref: 'disciplines' },
            { key: 'departmentId', label: 'Department', type: 'ref', ref: 'department' },
            {
                key: 'analyzerType',
                label: 'Type',
                type: 'enum',
                required: true,
                options: [
                    { value: 'MANUAL', label: 'Manual' },
                    { value: 'AUTOMATED', label: 'Automated' },
                    { value: 'POINT_OF_CARE', label: 'Point of care' },
                ],
            },
            {
                key: 'interfaceStatus',
                label: 'Interface',
                type: 'enum',
                required: true,
                options: [
                    { value: 'NOT_INTERFACED', label: 'Not interfaced' },
                    { value: 'PLANNED', label: 'Planned' },
                    { value: 'INTERFACED', label: 'Interfaced' },
                ],
                help: 'A record only. Choosing "Interfaced" does not connect anything.',
            },
        ],
    },
    'rejection-reasons': {
        key: 'rejection-reasons',
        label: 'Rejection reasons',
        singular: 'Rejection reason',
        blurb: 'Why a specimen can be turned away at receipt — haemolysed, insufficient quantity, wrong tube.',
        nameField: 'name',
        identityFields: ['code'],
        columns: ['specimenTypeId', 'requiresRecollection'],
        usage: ['LabSpecimen'],
        fields: [
            code(),
            { key: 'name', label: 'Reason', type: 'text', required: true, maxLength: 255 },
            { key: 'specimenTypeId', label: 'Only for specimen', type: 'ref', ref: 'specimen-types', help: 'Leave empty if the reason applies to any specimen.' },
            { key: 'requiresRecollection', label: 'Needs a fresh sample', type: 'boolean', help: 'When on, rejecting for this reason asks for a recollection.' },
            displayOrder,
        ],
    },
    'value-sets': {
        key: 'value-sets',
        label: 'Result value sets',
        singular: 'Value',
        blurb: 'The fixed answers a coded result can take — Positive / Negative, Reactive / Non-reactive — grouped by set.',
        nameField: 'displayValue',
        identityFields: ['valueSetCode', 'code'],
        identityLockedAfterCreate: true,
        columns: ['valueSetCode', 'isAbnormal', 'displayOrder'],
        usage: [],
        fields: [
            {
                key: 'valueSetCode',
                label: 'Value set',
                type: 'text',
                required: true,
                maxLength: 40,
                upper: true,
                help: 'The set this answer belongs to, e.g. POS_NEG. Answers with the same set code appear together.',
            },
            code(40, 'Unique within its set. Results refer to it, so it is fixed once created.'),
            { key: 'displayValue', label: 'Shown as', type: 'text', required: true, maxLength: 150 },
            { key: 'ordinal', label: 'Rank', type: 'integer', min: 0, max: 9999, help: 'Where this answer sits when answers are ordered by severity.' },
            { key: 'isAbnormal', label: 'Counts as abnormal', type: 'boolean', help: 'A result with this answer is flagged abnormal.' },
            displayOrder,
        ],
    },
    organisms: {
        key: 'organisms',
        label: 'Organisms',
        singular: 'Organism',
        blurb: 'Organisms a culture can isolate.',
        nameField: 'name',
        identityFields: ['code'],
        columns: ['gramStain'],
        usage: ['isolates'],
        fields: [
            code(40),
            { key: 'name', label: 'Name', type: 'text', required: true, maxLength: 255 },
            { key: 'gramStain', label: 'Gram stain', type: 'text', maxLength: 30, help: 'e.g. Positive, Negative.' },
        ],
    },
    antibiotics: {
        key: 'antibiotics',
        label: 'Antibiotics',
        singular: 'Antibiotic',
        blurb: 'Antibiotics tested in a sensitivity panel.',
        nameField: 'name',
        identityFields: ['code'],
        columns: ['class', 'inGramNegativePanel', 'inGramPositivePanel', 'inUrinePanel'],
        usage: ['susceptibilities'],
        fields: [
            code(40),
            { key: 'name', label: 'Name', type: 'text', required: true, maxLength: 255 },
            { key: 'class', label: 'Class', type: 'text', maxLength: 120, help: 'e.g. Penicillins, Cephalosporins.' },
            // STANDARD PANELS (desk, 2026-10-29): tick an antibiotic into a panel and the culture entry sheet lists it
            // automatically for that kind of organism — the technologist then only chooses the result for each row.
            { key: 'inGramNegativePanel', label: 'Gram-negative panel', type: 'boolean', help: 'Listed automatically when a Gram-negative organism is entered on a culture.' },
            { key: 'inGramPositivePanel', label: 'Gram-positive panel', type: 'boolean', help: 'Listed automatically when a Gram-positive organism (Staphylococcus, Streptococcus, Enterococcus) is entered.' },
            { key: 'inUrinePanel', label: 'Urine panel', type: 'boolean', help: 'Listed automatically on a urine culture.' },
        ],
    },
};
/** Human words for the relation names in `usage`, shown in the "Where used" tab. */
exports.LAB_MASTER_USAGE_LABELS = {
    tests: 'Tests',
    templates: 'Report templates',
    analyzers: 'Analyzers',
    LabParameter: 'Parameters',
    parameters: 'Parameters',
    tatTargets: 'Turnaround targets',
    children: 'Child specimen types',
    referenceRanges: 'Reference ranges',
    rejectionReasons: 'Rejection reasons',
    containers: 'Container links',
    testSpecimens: 'Test specimen rules',
    criticalValues: 'Critical values',
    deltaRules: 'Delta rules',
    LabSpecimen: 'Specimens collected',
    specimenTypes: 'Specimen types',
    specimenLinks: 'Container links',
    analyzerTests: 'Analyzer test links',
    testMethods: 'Test method links',
    isolates: 'Isolates',
    susceptibilities: 'Susceptibility results',
    testParameters: 'Tests',
    templateFields: 'Report template fields',
    LabResult: 'Results',
};
/** Kinds whose on/off switch is generic. Parameters keep their own create and edit routes but share the switch. */
exports.LAB_MASTER_ACTIVATION_KIND_KEYS = [...exports.LAB_MASTER_KIND_KEYS, 'parameters'];
const UUID = zod_1.z.string().uuid();
/** The zod rule for one field. `null` clears an optional field; a required one cannot be cleared. */
function fieldSchema(spec) {
    let base;
    switch (spec.type) {
        case 'text':
        case 'textarea': {
            let s = zod_1.z.string().trim();
            s = spec.required ? s.min(1, `${spec.label} is required`) : s;
            if (spec.maxLength)
                s = s.max(spec.maxLength, `${spec.label} is at most ${spec.maxLength} characters`);
            base = spec.upper ? s.transform((v) => v.toUpperCase()) : s;
            break;
        }
        case 'integer': {
            let n = zod_1.z.number().int();
            if (spec.min !== undefined)
                n = n.min(spec.min);
            if (spec.max !== undefined)
                n = n.max(spec.max);
            base = n;
            break;
        }
        case 'decimal': {
            let n = zod_1.z.number();
            if (spec.min !== undefined)
                n = n.min(spec.min);
            if (spec.max !== undefined)
                n = n.max(spec.max);
            base = n;
            break;
        }
        case 'boolean':
            base = zod_1.z.boolean();
            break;
        case 'enum':
            base = zod_1.z.enum((spec.options ?? []).map((o) => o.value));
            break;
        case 'ref':
            base = UUID;
            break;
        case 'date':
            base = zod_1.z.coerce.date();
            break;
    }
    return spec.required ? base : base.nullable();
}
/**
 * Creating a row. Every declared field is accepted; only `required` ones must be
 * present, and the free-text ones an empty string counts as "not given".
 */
function buildLabMasterCreateSchema(kind) {
    const shape = {};
    for (const spec of exports.LAB_MASTER_KINDS[kind].fields) {
        const rule = fieldSchema(spec);
        shape[spec.key] = spec.required ? rule : rule.optional();
    }
    return zod_1.z.object(shape).strict();
}
/**
 * Correcting a row. Every field is optional (absent = leave alone); a required
 * field may be replaced but never cleared. `expectedVersion` makes the write a
 * compare-and-swap, so two administrators editing the same row do not silently
 * overwrite each other.
 */
function buildLabMasterUpdateSchema(kind) {
    const shape = {};
    for (const spec of exports.LAB_MASTER_KINDS[kind].fields) {
        shape[spec.key] = fieldSchema(spec).optional();
    }
    shape.expectedVersion = zod_1.z.number().int().positive().optional();
    return zod_1.z.object(shape).strict();
}
exports.labMasterKindParamSchema = zod_1.z.object({ kind: zod_1.z.enum(exports.LAB_MASTER_KIND_KEYS) });
exports.labMasterItemParamSchema = exports.labMasterKindParamSchema.extend({ id: UUID });
/** Reading, history and the on/off switch also cover parameters. */
exports.labMasterAnyKindParamSchema = zod_1.z.object({ kind: zod_1.z.enum(exports.LAB_MASTER_ACTIVATION_KIND_KEYS) });
exports.labMasterAnyItemParamSchema = exports.labMasterAnyKindParamSchema.extend({ id: UUID });
exports.labMasterListQuerySchema = zod_1.z.object({
    page: zod_1.z.coerce.number().int().min(1).default(1),
    limit: zod_1.z.coerce.number().int().min(1).max(200).default(25),
    search: zod_1.z.string().trim().min(1).max(200).optional(),
    /** `all` is the default: an administrator must be able to find a switched-off row to switch it back on. */
    status: zod_1.z.enum(['all', 'active', 'inactive']).default('all'),
    /** A declared column, `isActive` or `updatedAt`. Anything else is ignored and the default order applies. */
    sort: zod_1.z.string().trim().min(1).max(40).optional(),
    direction: zod_1.z.enum(['asc', 'desc']).default('asc'),
});
exports.labMasterSetActiveBodySchema = zod_1.z.object({
    expectedVersion: zod_1.z.number().int().positive().optional(),
    reason: zod_1.z.string().trim().max(500).optional(),
});
//# sourceMappingURL=lab-masters-admin.request.js.map